// useRoomStatusMonitor.ts - Real-time room status monitoring for server-side game ending
// This ensures ALL players are notified when the server ends the game due to timer expiration

import { useEffect, useRef } from 'react';

import { supabase } from '../../../shared/services/supabaseService';

interface UseRoomStatusMonitorProps {
  roomId: string;
  onGameEnded: () => void;
}

export const useRoomStatusMonitor = ({ roomId, onGameEnded }: UseRoomStatusMonitorProps) => {
  const subscriptionRef = useRef<any>(null);
  const gameEndedRef = useRef(false);
  const onGameEndedRef = useRef(onGameEnded);
  const completedRoomIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    onGameEndedRef.current = onGameEnded;
  }, [onGameEnded]);

  useEffect(() => {
    if (!roomId) return;
    if (completedRoomIdsRef.current.has(roomId)) {
      console.log('[useRoomStatusMonitor] Skipping setup for completed room:', roomId);
      return;
    }

    console.log('[useRoomStatusMonitor] Setting up room status monitoring for:', roomId);

    // Subscribe to room status changes
    subscriptionRef.current = supabase
      .channel(`room_status_${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'multiplayer_game_rooms',
          filter: `id=eq.${roomId}`,
        },
        (payload) => {
          console.log('[useRoomStatusMonitor] Room status change detected:', payload);
          
          if (payload.new?.status === 'completed' && !gameEndedRef.current) {
            console.log('[useRoomStatusMonitor] Game ended by server - triggering callback');
            gameEndedRef.current = true;
            completedRoomIdsRef.current.add(roomId);
            onGameEndedRef.current();
          }
        }
      )
      .subscribe((status) => {
        console.log('[useRoomStatusMonitor] Subscription status:', status);
      });

    // Also listen to the custom notification channel
    const notificationChannel = supabase
      .channel('room_notifications')
      .on(
        'broadcast',
        { event: 'room_status_change' },
        (payload) => {
          console.log('[useRoomStatusMonitor] Room notification received:', payload);
          
          if (payload.payload?.room_id === roomId && 
              payload.payload?.status === 'completed' && 
              !gameEndedRef.current) {
            console.log('[useRoomStatusMonitor] Game ended via notification - triggering callback');
            gameEndedRef.current = true;
            completedRoomIdsRef.current.add(roomId);
            onGameEndedRef.current();
          }
        }
      )
      .subscribe();

    return () => {
      console.log('[useRoomStatusMonitor] Cleaning up room status monitoring');
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      notificationChannel.unsubscribe();
    };
  }, [roomId]);

  // Periodic check as fallback (every 5 seconds)
  useEffect(() => {
    if (!roomId || gameEndedRef.current || completedRoomIdsRef.current.has(roomId)) return;

    const interval = setInterval(async () => {
      try {
        const { data: room, error } = await supabase
          .from('multiplayer_game_rooms')
          .select('status')
          .eq('id', roomId)
          .single();

        if (!error && room?.status === 'completed' && !gameEndedRef.current) {
          console.log('[useRoomStatusMonitor] Game completed detected via polling');
          gameEndedRef.current = true;
          completedRoomIdsRef.current.add(roomId);
          onGameEndedRef.current();
        }
      } catch (error) {
        console.error('[useRoomStatusMonitor] Error polling room status:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [roomId]);
};
